import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useCookie } from 'react-use'
import { Coordinates } from 'shared-types'
import { useSession } from '~/hooks'
import { CookieKeys } from '~/constants/cookieKeys'
import { Client } from '~/customClients/client'
import { UserLocationContextProps } from './UserLocationProvider.types'

export const UserLocationContext = createContext<UserLocationContextProps>({
  isGeolocating: false,
  lastGeolocation: undefined,
})

const geolocateByBrowser = async (): Promise<Coordinates> => {
  if (!navigator.geolocation) {
    throw new Error('Browser geolocation not supported')
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (position.coords) {
          resolve(position.coords)
        } else {
          reject()
        }
      },
      () => {
        reject()
      }
    )
  })
}

const parseGeolocationCookie = (value?: string): Coordinates | undefined => {
  if (!value) {
    return
  }
  try {
    const [latitude, longitude] = value.split(',')
    return {
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
    }
  } catch {}
}

const composeGeolocationCookie = (location: Coordinates): string => {
  return `${location.latitude},${location.longitude}`
}

export const UserLocationProvider = ({ children }: PropsWithChildren) => {
  const [isGeolocating, setIsGeolocating] = useState(false)
  const { session } = useSession()

  const [lastGeolocationCookieValue, setLastGeolocationCookieValue] = useCookie(
    CookieKeys.geolocation
  )

  const lastGeolocation = useMemo<Coordinates | undefined>(() => {
    return parseGeolocationCookie(lastGeolocationCookieValue)
  }, [lastGeolocationCookieValue])

  const setLastGeolocation = useCallback(
    (location: Coordinates) => {
      if (!location) {
        return
      }
      setLastGeolocationCookieValue(composeGeolocationCookie(location))
    },
    [setLastGeolocationCookieValue]
  )

  const geolocate = useCallback(
    async (
      eager = false,
      skipBrowser = false
    ): Promise<Coordinates | undefined> => {
      let location: Coordinates

      // Return last geolocation if eager and available
      if (eager && lastGeolocation) {
        return lastGeolocation
      }

      setIsGeolocating(true)

      if (!skipBrowser) {
        try {
          location = await geolocateByBrowser()
        } catch (err) {
          console.warn(err)
        }
      }

      setLastGeolocation(location)
      setIsGeolocating(false)
      return location
    },
    [lastGeolocation, setLastGeolocation]
  )

  const geolocateWithPermission = useCallback(async (): Promise<
    Coordinates | undefined
  > => {
    let location: Coordinates
    try {
      location = await geolocateByBrowser()
    } catch (err) {
      console.warn(err)
    }

    return location
  }, [])

  const searchLocations = useCallback(
    async (query: string, countryCode: string) => {
      if (!query || !countryCode) {
        return []
      }

      try {
        const results = await Client.store.searchLocations(
          session,
          query,
          countryCode
        )

        return results.getValue() || []
      } catch (error) {
        return []
      }
    },
    [session]
  )

  const geolocateFromInternalService = useCallback(
    async (keyword: string, countryCode = 'AU') => {
      if (!keyword) {
        return []
      }

      try {
        const results = await Client.store.getPostCodeData(
          session,
          keyword,
          countryCode
        )

        return results.getValue() || []
      } catch (error) {
        return []
      }
    },
    [session]
  )

  const result = useMemo(() => {
    return {
      isGeolocating,
      lastGeolocation,
      geolocate,
      geolocateWithPermission,
      geolocateFromInternalService,
      searchLocations,
    }
  }, [
    isGeolocating,
    lastGeolocation,
    geolocate,
    geolocateWithPermission,
    geolocateFromInternalService,
    searchLocations,
  ])

  return (
    <UserLocationContext.Provider value={result}>
      {children}
    </UserLocationContext.Provider>
  )
}
